/**
 * Panel del catálogo — /admin
 *
 * Lo usa la dueña, casi siempre desde el celular. Decisiones de UX que vienen de eso:
 *
 *  - Reordenar con botones ↑ ↓ y NO con drag & drop: arrastrar en pantalla táctil es
 *    frustrante y poco preciso. El plan decía "drag-reorder"; se cambió a favor de la
 *    usuaria real.
 *  - La foto se ve recortada ANTES de guardar, con un control para mover el recorte.
 *    La card del sitio es 3:4 y usa object-cover, así que sin esto el recorte pasa a
 *    ciegas y ella no se entera de qué se perdió.
 *  - Nada de "¿estás seguro?" para ocultar: el borrado es lógico y reversible con un clic.
 *
 * Autenticación: no hay nada acá. Cloudflare Access ya exigió el código por correo
 * antes de que esta página cargue.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import * as api from '@/lib/api';
import { comprimir, decodificar, previsualizar, ErrorImagen, calcularRecorte } from '@/lib/imagen';
import { aCentavos, aPesos, aPiezas, formatearCorto, ErrorPrecio } from '@/lib/precio';
import AjustesTienda from '@/components/AjustesTienda';

const GENEROS = ['Hombre', 'Mujer', 'Unisex'];
// '' = sin clasificar. Es una opción real del selector, no un placeholder: un
// producto sin categoría sigue saliendo en el sitio, en el filtro "Todas".
const CATEGORIAS = ['Diseñador', 'Nicho', 'Árabe'];
const VACIO = { brand: '', name: '', note: '', gender: 'Unisex', category: '', precio: '', tachado: '', piezas: '', pay_url: '' };
const kb = (n) => `${Math.round(n / 1024)} KB`;

export default function Admin() {
  const [productos, setProductos] = useState(null);
  const [error, setError] = useState(null);
  const [aviso, setAviso] = useState(null);
  const [editando, setEditando] = useState(null); // null | 'nuevo' | id
  const [ocupado, setOcupado] = useState(false);

  /* --- reordenar -------------------------------------------------------------
     Dos cosas que antes eran caras y ahora no:

     1. Cada toque de flecha mandaba las 54 posiciones (163 parámetros) MÁS un GET
        del catálogo entero. Subir un perfume desde el final eran 53 POSTs. Ahora se
        manda solo lo que cambió (un intercambio son 2 filas) y no se recarga: la
        lista de la pantalla ya es el orden correcto.
     2. Los toques seguidos se agrupan: se guarda una sola vez cuando dejás de
        mover. Arrastrar de abajo hasta arriba = 1 escritura, no 53.

     `ordenGuardado` es el orden que YA está en la base; el delta se calcula contra
     él y solo se actualiza cuando el guardado sale bien, así un fallo se reintenta
     completo en el próximo movimiento en vez de perderse. */
  const ordenGuardado = useRef([]);
  const temporizador = useRef(null);
  const [estadoOrden, setEstadoOrden] = useState(null); // null | 'pendiente' | 'guardado'

  /* `listaRef` y `origenRef` existen porque el orden se toca desde eventos que pueden
     llegar en el mismo ciclo de render (dos clics muy seguidos, o el dragstart y el
     drop de un arrastre). Leyendo el estado por closure, el segundo evento ve la lista
     de ANTES del primero y se pierde un movimiento — se vio en la prueba del panel,
     disparando los eventos en el mismo tick. Con refs cada evento parte del estado
     real, sin depender de que React ya haya re-renderizado. */
  const listaRef = useRef([]);
  const origenRef = useRef(null);

  const cargar = useCallback(async () => {
    try {
      setError(null);
      const { products } = await api.listar();
      setProductos(products);
      listaRef.current = products;
      ordenGuardado.current = products.map((x) => x.id);
    } catch (e) {
      setError(e.message);
    }
  }, []);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const conAviso = async (fn, mensaje) => {
    setOcupado(true);
    setError(null);
    try {
      await fn();
      await cargar();
      if (mensaje) {
        setAviso(mensaje);
        setTimeout(() => setAviso(null), 4000);
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setOcupado(false);
    }
  };

  /** Solo los productos cuya posición difiere de la que está en la base. */
  const deltaDeOrden = (lista) => {
    const antes = new Map(ordenGuardado.current.map((id, i) => [id, i + 1]));
    return lista
      .map((p, i) => ({ id: p.id, position: i + 1 }))
      .filter((x) => antes.get(x.id) !== x.position);
  };

  const guardarOrden = async () => {
    const lista = listaRef.current;
    const items = deltaDeOrden(lista);
    if (!items.length) return setEstadoOrden(null);
    try {
      setError(null);
      await api.reordenar(items);
      ordenGuardado.current = lista.map((p) => p.id);
      setEstadoOrden('guardado');
      setTimeout(() => setEstadoOrden((e) => (e === 'guardado' ? null : e)), 2500);
    } catch (e) {
      setError(e.message);
      setEstadoOrden(null); // ordenGuardado NO se toca: el próximo intento reenvía todo
    }
  };

  const reordenarLocal = (lista) => {
    listaRef.current = lista;
    setProductos(lista); // optimista: la lista se acomoda al instante
    setEstadoOrden('pendiente');
    clearTimeout(temporizador.current);
    // Un solo guardado cuando deja de mover: arrastrar de abajo hasta arriba es una
    // escritura, no una por paso.
    temporizador.current = setTimeout(guardarOrden, 700);
  };

  const mover = (indice, delta) => {
    const actual = listaRef.current;
    const otro = indice + delta;
    if (otro < 0 || otro >= actual.length) return;
    const lista = [...actual];
    [lista[indice], lista[otro]] = [lista[otro], lista[indice]];
    reordenarLocal(lista);
  };

  // --- arrastrar y soltar (mouse; en el celular quedan las flechas) ---
  const [arrastrando, setArrastrando] = useState(null);
  const [sobre, setSobre] = useState(null);

  const soltar = (destino) => {
    const origen = origenRef.current;
    origenRef.current = null;
    setArrastrando(null);
    setSobre(null);
    if (origen === null || origen === destino) return;
    const lista = [...listaRef.current];
    const [movido] = lista.splice(origen, 1);
    lista.splice(destino, 0, movido);
    reordenarLocal(lista);
  };

  /* El guardado está diferido, así que cerrar la pestaña en esos 700 ms perdería el
     orden en silencio. Preferimos avisar antes que perderlo sin que se entere. */
  useEffect(() => {
    if (estadoOrden !== 'pendiente') return;
    const avisar = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', avisar);
    return () => window.removeEventListener('beforeunload', avisar);
  }, [estadoOrden]);

  const alternarEstado = (p) =>
    conAviso(
      () =>
        p.status === 'published'
          ? api.ocultar(p.id)
          : api.editar(p.id, { status: 'published' }),
      p.status === 'published' ? 'Producto oculto del sitio.' : 'Producto publicado.'
    );

  return (
    <div className="min-h-screen bg-[#050505] text-neutral-200">
      <header className="border-b border-[#1a1a1a] px-5 py-4 sticky top-0 bg-[#050505]/95 backdrop-blur z-10">
        <div className="max-w-3xl mx-auto flex items-center justify-between gap-4">
          <div>
            <h1 className="font-serif-lux text-2xl text-gold-gradient leading-tight">Catálogo</h1>
            <p className="text-xs text-neutral-500 mt-0.5">
              {productos ? `${productos.length} fragancias · ${productos.filter((p) => p.status === 'published').length} visibles` : 'Cargando…'}
              {/* El guardado del orden es diferido; sin este indicador el usuario no
                  sabe si su movimiento quedó. */}
              {estadoOrden === 'pendiente' && (
                <span className="ml-2 text-neutral-400" data-testid="orden-pendiente">· guardando orden…</span>
              )}
              {estadoOrden === 'guardado' && (
                <span className="ml-2 text-[#d6ad60]" data-testid="orden-guardado">· orden guardado</span>
              )}
            </p>
          </div>
          <Link to="/" className="text-xs uppercase tracking-widest text-neutral-400 hover:text-[#d6ad60]">
            Ver sitio
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-5 py-6">
        {aviso && (
          <p className="mb-4 border border-[#d6ad60]/40 bg-[#d6ad60]/10 px-4 py-3 text-sm text-[#e5c37a]">
            {aviso}
          </p>
        )}
        {error && (
          <div className="mb-4 border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-300">
            <p>{error}</p>
            <button onClick={cargar} className="mt-2 underline underline-offset-2">
              Reintentar
            </button>
          </div>
        )}

        <AjustesTienda />

        {editando === 'nuevo' ? (
          <Formulario
            onCancelar={() => setEditando(null)}
            onGuardar={(datos) =>
              conAviso(async () => {
                await api.crear(datos);
                setEditando(null);
              }, 'Fragancia agregada.')
            }
          />
        ) : (
          <button
            onClick={() => setEditando('nuevo')}
            disabled={ocupado}
            className="w-full mb-6 border border-[#d6ad60] px-5 py-4 text-sm uppercase tracking-widest text-[#d6ad60] hover:bg-[#d6ad60] hover:text-black transition-colors disabled:opacity-40"
          >
            + Agregar fragancia
          </button>
        )}

        {productos === null && !error && <p className="text-neutral-500 text-sm">Cargando catálogo…</p>}

        <ul className="space-y-3">
          {(productos || []).map((p, i) =>
            editando === p.id ? (
              <li key={p.id}>
                <Formulario
                  producto={p}
                  onCancelar={() => setEditando(null)}
                  onGuardar={(datos) =>
                    conAviso(async () => {
                      await api.editar(p.id, datos);
                      setEditando(null);
                    }, 'Cambios guardados.')
                  }
                />
              </li>
            ) : (
              <li
                key={p.id}
                draggable={!ocupado}
                /* Si el arrastre empieza sobre un botón o un campo, se cancela: sin
                   esto, intentar tocar "Editar" a veces arrastra la fila. */
                onDragStart={(e) => {
                  if (e.target.closest('button, input, select, a')) {
                    e.preventDefault();
                    return;
                  }
                  origenRef.current = i;
                  setArrastrando(i);
                  e.dataTransfer.effectAllowed = 'move';
                  // Firefox no inicia el arrastre si no se setea algún dato.
                  e.dataTransfer.setData('text/plain', p.id);
                }}
                onDragOver={(e) => {
                  if (origenRef.current === null) return;
                  e.preventDefault();
                  e.dataTransfer.dropEffect = 'move';
                  if (sobre !== i) setSobre(i);
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  soltar(i);
                }}
                onDragEnd={() => {
                  origenRef.current = null;
                  setArrastrando(null);
                  setSobre(null);
                }}
                className={`flex gap-3 border bg-[#0a0a0a] p-3 transition-colors ${
                  p.status === 'hidden' ? 'opacity-50' : ''
                } ${arrastrando === i ? 'opacity-30' : ''} ${
                  sobre === i && arrastrando !== null && arrastrando !== i
                    ? 'border-[#d6ad60]'
                    : 'border-[#1a1a1a]'
                }`}
                data-testid={`fila-${p.id}`}
              >
                {/* Agarradera: señala que la fila se puede arrastrar. El arrastre lo
                    acepta toda la fila, así que no hace falta apuntarle fino. */}
                <span
                  aria-hidden="true"
                  title="Arrastrá para mover"
                  className="shrink-0 self-center text-neutral-600 cursor-grab active:cursor-grabbing select-none px-1 text-lg leading-none"
                >
                  ⠿
                </span>
                <div className="w-16 shrink-0 aspect-[3/4] bg-[#111] overflow-hidden">
                  {p.img_key ? (
                    <img
                      src={`https://img.odyscents.com/${p.img_key}`}
                      alt=""
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  ) : (
                    <span className="flex h-full items-center justify-center text-[10px] text-neutral-600 text-center px-1">
                      sin foto
                    </span>
                  )}
                </div>

                <div className="min-w-0 flex-1">
                  <p className="text-[10px] uppercase tracking-widest text-[#d6ad60]">{p.brand}</p>
                  <p className="font-serif-lux text-lg leading-tight truncate">{p.name}</p>
                  <p className="text-xs text-neutral-500">
                    {p.note} · {p.gender}
                    {p.category && <span className="text-neutral-400"> · {p.category}</span>}
                    {p.compare_at_cents != null && (
                      <span className="text-neutral-400">
                        {' '}· antes <span className="line-through">{formatearCorto(p.compare_at_cents)}</span>
                      </span>
                    )}
                    {p.status === 'hidden' && <span className="ml-2 text-neutral-400">(oculta)</span>}
                  </p>

                  <p className="mt-1 text-xs flex flex-wrap items-center gap-x-3 gap-y-1">
                    <span className={p.price_cents == null ? 'text-neutral-600' : 'text-[#e5c37a]'}>
                      {p.price_cents == null ? 'sin precio' : formatearCorto(p.price_cents)}
                    </span>
                    <span className={p.stock === 0 ? 'text-red-300' : 'text-neutral-400'}>
                      {p.stock == null
                        ? 'sin inventario'
                        : p.stock === 0
                          ? 'AGOTADO'
                          : `${p.stock} ${p.stock === 1 ? 'pieza' : 'piezas'}`}
                    </span>
                    {/* El estado que confunde: precio puesto, piezas disponibles, pero
                        sin link => el sitio muestra precio y NO deja pagar. */}
                    {p.price_cents != null && p.stock !== 0 && !p.pay_url && (
                      <span className="text-amber-300">falta link de pago</span>
                    )}
                  </p>

                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <button onClick={() => setEditando(p.id)} disabled={ocupado} className={btn}>
                      Editar
                    </button>
                    <button onClick={() => alternarEstado(p)} disabled={ocupado} className={btn}>
                      {p.status === 'published' ? 'Ocultar' : 'Publicar'}
                    </button>
                  </div>
                </div>

                <div className="flex flex-col justify-center gap-1 shrink-0">
                  <button
                    onClick={() => mover(i, -1)}
                    disabled={ocupado || i === 0}
                    aria-label="Subir"
                    className={flecha}
                  >
                    ↑
                  </button>
                  <button
                    onClick={() => mover(i, 1)}
                    disabled={ocupado || i === productos.length - 1}
                    aria-label="Bajar"
                    className={flecha}
                  >
                    ↓
                  </button>
                </div>
              </li>
            )
          )}
        </ul>

        {productos?.length === 0 && (
          <p className="text-neutral-500 text-sm">Todavía no hay fragancias. Agregá la primera.</p>
        )}
      </main>
    </div>
  );
}

const btn =
  'border border-[#2a2a2a] px-3 py-1.5 text-xs uppercase tracking-wider text-neutral-300 hover:border-[#d6ad60] hover:text-[#d6ad60] transition-colors disabled:opacity-40';
const flecha =
  'border border-[#2a2a2a] w-9 h-9 text-neutral-300 hover:border-[#d6ad60] hover:text-[#d6ad60] transition-colors disabled:opacity-25';
// placeholder bien atenuado: con el contraste por defecto se confunde con un valor
// ya escrito (me pasó revisando las capturas).
const input =
  'w-full bg-[#0d0d0d] border border-[#2a2a2a] px-3 py-2.5 text-sm text-neutral-100 placeholder:text-neutral-600 focus:border-[#d6ad60] outline-none';

function Formulario({ producto, onCancelar, onGuardar }) {
  const [datos, setDatos] = useState(() =>
    producto
      ? {
          brand: producto.brand,
          name: producto.name,
          note: producto.note,
          gender: producto.gender,
          category: producto.category || '',
          // Los tres nuevos viven en el form como TEXTO y se convierten al guardar:
          // así el input no pelea con el usuario mientras escribe "1" de "1500".
          precio: aPesos(producto.price_cents),
          tachado: aPesos(producto.compare_at_cents),
          piezas: producto.stock == null ? '' : String(producto.stock),
          pay_url: producto.pay_url || '',
        }
      : VACIO
  );
  const [foto, setFoto] = useState(null); // {bitmap, preview, offset, original, recortado}
  const [subiendo, setSubiendo] = useState(false);
  const [errorFoto, setErrorFoto] = useState(null);
  const [errorDatos, setErrorDatos] = useState(null);
  const fileRef = useRef(null);

  const set = (campo) => (e) => setDatos({ ...datos, [campo]: e.target.value });

  const elegirFoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setErrorFoto(null);
    try {
      const bitmap = await decodificar(file);
      const { recortado } = calcularRecorte(bitmap.width, bitmap.height);
      const preview = await previsualizar(bitmap, 0.5);
      setFoto({ bitmap, preview, offset: 0.5, original: file.size, recortado });
    } catch (err) {
      setErrorFoto(err instanceof ErrorImagen ? err.message : 'No se pudo leer la foto.');
      setFoto(null);
    }
  };

  const moverRecorte = async (e) => {
    const offset = Number(e.target.value);
    const preview = await previsualizar(foto.bitmap, offset);
    setFoto({ ...foto, offset, preview });
  };

  const enviar = async (e) => {
    e.preventDefault();
    setErrorFoto(null);
    setErrorDatos(null);

    // Precio y piezas se convierten ANTES de subir la foto: si hay un dedazo en el
    // precio, no tiene sentido gastar la subida a R2 para después fallar.
    let price_cents, compare_at_cents, stock;
    try {
      price_cents = aCentavos(datos.precio);
      compare_at_cents = aCentavos(datos.tachado);
      stock = aPiezas(datos.piezas);
    } catch (err) {
      setErrorDatos(err instanceof ErrorPrecio ? err.message : 'Revisá el precio y las piezas.');
      return;
    }

    /* La regla cruzada también se comprueba acá, no solo en el servidor: es un error
       que se arregla mirando el formulario, y hacerlo ir y volver para enterarse es
       peor. El servidor sigue siendo el que manda. */
    if (compare_at_cents != null) {
      if (price_cents == null) {
        setErrorDatos('Para poner un precio tachado, primero ponele precio al producto.');
        return;
      }
      if (compare_at_cents <= price_cents) {
        setErrorDatos(
          `El precio tachado (${formatearCorto(compare_at_cents)}) tiene que ser MAYOR al de venta (${formatearCorto(price_cents)}).`
        );
        return;
      }
    }

    let extra = {};
    if (foto) {
      setSubiendo(true);
      try {
        const blob = await comprimir(foto.bitmap, foto.offset);
        const { key } = await api.subirImagen(blob);
        extra = { img_key: key, img_w: 800, img_h: 1066 };
      } catch (err) {
        setErrorFoto(err.message);
        setSubiendo(false);
        return;
      }
      setSubiendo(false);
    }

    // Payload explícito: `precio`/`piezas` son del formulario, no del API.
    onGuardar({
      brand: datos.brand,
      name: datos.name,
      note: datos.note,
      gender: datos.gender,
      category: datos.category,
      price_cents,
      compare_at_cents,
      stock,
      pay_url: datos.pay_url.trim(),
      ...extra,
    });
  };

  return (
    <form onSubmit={enviar} className="border border-[#d6ad60]/40 bg-[#0a0a0a] p-4 mb-6 space-y-3">
      <p className="font-serif-lux text-lg text-[#e5c37a]">
        {producto ? `Editar ${producto.name}` : 'Nueva fragancia'}
      </p>

      <label className="block">
        <span className="text-[10px] uppercase tracking-widest text-neutral-500">Marca</span>
        <input className={input} value={datos.brand} onChange={set('brand')} required maxLength={120} placeholder="Dior" />
      </label>

      <label className="block">
        <span className="text-[10px] uppercase tracking-widest text-neutral-500">Nombre</span>
        <input className={input} value={datos.name} onChange={set('name')} required maxLength={120} placeholder="Sauvage" />
      </label>

      <label className="block">
        <span className="text-[10px] uppercase tracking-widest text-neutral-500">Notas</span>
        <input className={input} value={datos.note} onChange={set('note')} required maxLength={120} placeholder="Amaderado · Especiado" />
      </label>

      <label className="block">
        <span className="text-[10px] uppercase tracking-widest text-neutral-500">Para</span>
        <select className={input} value={datos.gender} onChange={set('gender')}>
          {GENEROS.map((g) => (
            <option key={g} value={g}>
              {g}
            </option>
          ))}
        </select>
      </label>

      <label className="block">
        <span className="text-[10px] uppercase tracking-widest text-neutral-500">Categoría</span>
        <select className={input} value={datos.category} onChange={set('category')}>
          <option value="">Sin clasificar</option>
          {CATEGORIAS.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
        <span className="mt-1 block text-[10px] text-neutral-600">
          Sale como filtro en el sitio. Los filtros de Nicho y Árabe aparecen solos
          cuando subas el primero de cada uno.
        </span>
      </label>

      {/* inputMode en los dos numéricos: ella entra desde el celular y sin esto
          el teclado sale alfabético y escribir un precio es un suplicio. */}
      <div className="grid grid-cols-2 gap-3">
        <label className="block">
          <span className="text-[10px] uppercase tracking-widest text-neutral-500">Precio (MXN)</span>
          <input
            className={input}
            value={datos.precio}
            onChange={set('precio')}
            inputMode="decimal"
            maxLength={12}
            placeholder="1500"
          />
          <span className="mt-1 block text-[10px] text-neutral-600">
            Vacío = "Consultar por WhatsApp"
          </span>
        </label>

        <label className="block">
          <span className="text-[10px] uppercase tracking-widest text-neutral-500">Piezas</span>
          <input
            className={input}
            value={datos.piezas}
            onChange={set('piezas')}
            inputMode="numeric"
            maxLength={4}
            placeholder="3"
          />
          <span className="mt-1 block text-[10px] text-neutral-600">
            0 = Agotado · vacío = no lo controlás
          </span>
        </label>
      </div>

      <label className="block">
        <span className="text-[10px] uppercase tracking-widest text-neutral-500">
          Precio tachado (opcional)
        </span>
        <input
          className={input}
          value={datos.tachado}
          onChange={set('tachado')}
          inputMode="decimal"
          placeholder="4200"
        />
        <span className="mt-1 block text-[10px] text-neutral-600">
          Se muestra tachado al lado del precio. Tiene que ser MAYOR al de venta.
          Vacío = no se muestra nada tachado.
        </span>
      </label>

      <label className="block">
        <span className="text-[10px] uppercase tracking-widest text-neutral-500">
          Link de pago (Clip o Mercado Pago)
        </span>
        <input
          className={input}
          value={datos.pay_url}
          onChange={set('pay_url')}
          type="url"
          maxLength={500}
          placeholder="https://pago.clip.mx/v3/..."
        />
        <span className="mt-1 block text-[10px] text-neutral-600">
          Generá el link en tu app de Clip y pegalo acá. Uno por producto: el link
          cobra un monto fijo, así que si repetís el mismo en dos perfumes, los dos
          cobran lo mismo. Podés cambiarlo cuando quieras. Sin link, el sitio solo
          ofrece WhatsApp.
        </span>
      </label>

      <div className="pt-1">
        <span className="text-[10px] uppercase tracking-widest text-neutral-500">Foto</span>
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          onChange={elegirFoto}
          className="mt-1 block w-full text-xs text-neutral-400 file:mr-3 file:border file:border-[#2a2a2a] file:bg-transparent file:px-3 file:py-2 file:text-xs file:uppercase file:tracking-wider file:text-[#d6ad60]"
        />

        {errorFoto && <p className="mt-2 text-xs text-red-300">{errorFoto}</p>}

        {foto && (
          <div className="mt-3 flex gap-3">
            <img src={foto.preview} alt="Previsualización" className="w-24 aspect-[3/4] object-cover border border-[#2a2a2a]" />
            <div className="text-xs text-neutral-500 space-y-2 flex-1">
              <p>
                Así se va a ver en el sitio. Original {kb(foto.original)} → se guarda en unos 60-90 KB.
              </p>
              {foto.recortado === 'nada' ? (
                <p className="text-neutral-400">La foto ya tiene la proporción correcta: no se recorta nada.</p>
              ) : (
                <label className="block">
                  <span>
                    Mover el recorte ({foto.recortado === 'ancho' ? 'izquierda ↔ derecha' : 'arriba ↕ abajo'})
                  </span>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.02"
                    value={foto.offset}
                    onChange={moverRecorte}
                    className="w-full accent-[#d6ad60]"
                  />
                </label>
              )}
            </div>
          </div>
        )}
      </div>

      {errorDatos && (
        <p className="border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-300">
          {errorDatos}
        </p>
      )}

      <div className="flex gap-2 pt-2">
        <button
          type="submit"
          disabled={subiendo}
          className="border border-[#d6ad60] bg-[#d6ad60] px-5 py-2.5 text-xs uppercase tracking-widest text-black hover:bg-[#e5c37a] disabled:opacity-50"
        >
          {subiendo ? 'Subiendo foto…' : 'Guardar'}
        </button>
        <button type="button" onClick={onCancelar} disabled={subiendo} className={btn}>
          Cancelar
        </button>
      </div>
    </form>
  );
}
